import i18n from './i18n'
import { EQUAL_STRING, EQUAL_STRING_CONSTRAINT } from './mustBeEqualString'
import { SOFT_PASSWORD } from './mustBeSoftPassword'
import { STRONG_PASSWORD } from './mustBeStrongPassword'
import type {
  EMAIL_ADDRESS,
  PROFESSIONAL_EMAIL_ADDRESS,
} from './mustBeEmailAddress'
import type {
  HIGHER_OR_EQUAL,
  HIGHER_OR_EQUAL_CONSTRAINT,
} from './mustBeHigherOrEqualAt'
import type {
  LOWER_OR_EQUAL,
  LOWER_OR_EQUAL_CONSTRAINT,
} from './mustBeLowerOrEqualAt'
import type { MAX_LENGTH, MAX_LENGTH_CONSTRAINT } from './mustBeMaxLength'
import type { MIN_LENGTH, MIN_LENGTH_CONSTRAINT } from './mustBeMinLength'
import type { NUMBER } from './mustBeNumber'
import type { NUMBER_OR_UNDEFINED } from './mustBeNumberOrUndefined'
import type { REQUIRED } from './mustBeRequired'
import type { Locales } from '@captaincause/types'

export const ERRORS = {
  REQUIRED: 'REQUIRED',
  EMAIL_ADDRESS: 'EMAIL_ADDRESS',
  PROFESSIONAL_EMAIL_ADDRESS: 'PROFESSIONAL_EMAIL_ADDRESS',
  NUMBER: 'NUMBER',
  NUMBER_OR_UNDEFINED: 'NUMBER_OR_UNDEFINED',
  MIN_LENGTH: 'MIN_LENGTH',
  MAX_LENGTH: 'MAX_LENGTH',
  LOWER_OR_EQUAL_TO: 'LOWER_OR_EQUAL_TO',
  LOWER_THAN: 'LOWER_THAN',
  HIGHER_OR_EQUAL_TO: 'HIGHER_OR_EQUAL_TO',
  HIGHER_THAN: 'HIGHER_THAN',
  STRONG_PASSWORD: 'STRONG_PASSWORD',
  SOFT_PASSWORD: 'SOFT_PASSWORD',
  EQUAL_STRING: 'EQUAL_STRING',
} as const

export type Errors = keyof typeof ERRORS

export type Constraints =
  | MAX_LENGTH_CONSTRAINT
  | MIN_LENGTH_CONSTRAINT
  | LOWER_OR_EQUAL_CONSTRAINT
  | HIGHER_OR_EQUAL_CONSTRAINT
  | EQUAL_STRING_CONSTRAINT

type Error = {
  error: string | undefined
  constraints: any
}

type WithConstraint<F, C> = {
  fn: F
  constraints: C
}

export type Validators = Partial<{
  mustBeStrongPassword: STRONG_PASSWORD
  mustBeSoftPassword: SOFT_PASSWORD
  mustBeBlankOrEmailAddress: EMAIL_ADDRESS
  mustBeEmailAddress: EMAIL_ADDRESS
  mustBeProfessionnalEmailAddress: PROFESSIONAL_EMAIL_ADDRESS
  mustBeNumber: NUMBER
  mustBeNumberOrUndefined: NUMBER_OR_UNDEFINED
  mustBeRequired: REQUIRED
  mustBeEqualString: WithConstraint<EQUAL_STRING, EQUAL_STRING_CONSTRAINT>
  mustBeLowerOrEqualAt: WithConstraint<
    LOWER_OR_EQUAL,
    LOWER_OR_EQUAL_CONSTRAINT
  >
  mustBeMaxLength: WithConstraint<MAX_LENGTH, MAX_LENGTH_CONSTRAINT>
  mustBeMinLength: WithConstraint<MIN_LENGTH, MIN_LENGTH_CONSTRAINT>
  mustBeHigherOrEqualAt: WithConstraint<
    HIGHER_OR_EQUAL,
    HIGHER_OR_EQUAL_CONSTRAINT
  >
}>

type CustomValidators = {
  customs: ((value: string) => string | undefined)[]
}

export type ValidatorsAndCustoms = Partial<Validators & CustomValidators>

function reduceValidatorsToError(
  validators: Validators[keyof Validators][],
  value: string,
): Error {
  return validators.reduce(
    (reducedError: Error, validator) => {
      if (reducedError.error) return reducedError

      if (validator && typeof validator === 'function') {
        return {
          error: validator(value),
          constraints: undefined,
        }
      }

      if (validator && 'fn' in validator && 'constraints' in validator) {
        const constraints = { ...validator.constraints }

        return {
          error: validator.fn(value, constraints),
          constraints,
        }
      }

      return reducedError
    },
    { error: undefined, constraints: undefined },
  )
}

const composeValidators =
  (validators: ValidatorsAndCustoms, locale: Locales) =>
  (v: string | number) => {
    const value = typeof v === 'number' ? v.toFixed() : v

    const { customs: customValidators, ...mappedValidators } = validators

    const mappedValidatorsError = reduceValidatorsToError(
      Object.values(mappedValidators),
      value,
    )

    if (mappedValidatorsError.error) {
      const label =
        i18n[locale][
          mappedValidatorsError.error as keyof (typeof i18n)[typeof locale]
        ]

      if (!label) return mappedValidatorsError.error

      return typeof label === 'function'
        ? label(mappedValidatorsError.constraints)
        : label
    }

    if (customValidators) {
      const customValidatorsError = reduceValidatorsToError(
        customValidators ?? [],
        value,
      )

      if (customValidatorsError) return customValidatorsError.error
    }

    return undefined
  }

export function validate(validators: ValidatorsAndCustoms, locale: Locales) {
  return composeValidators(validators, locale)
}
